// AI Skill Platform — Skill Management types

// Auto-invocation trigger types (handoff worktree-binary-enchanting-bonbon)
type HttpTrigger = { type: 'http' };
type CronTrigger = { type: 'cron'; cron: string; message?: string; timezone?: string; org_scope?: 'all' | 'current' };
type NotifyTrigger = { type: 'notify'; channel?: string };
export type SkillTrigger = HttpTrigger | CronTrigger | NotifyTrigger;

export interface SkillToolHttpConfig {
  // HTTP mode (used by UI-created per-org skills)
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  url?: string;
  path_params?: string[];
  params_mapping?: Record<string, string>;
  body_mapping?: Record<string, string>;
  // SQL mode (read-only in UI; only system skills from .md files use this)
  mode?: 'sql';
  query_name?: string;
}

export interface SkillToolInputSchema {
  type: 'object';
  properties: Record<
    string,
    {
      type: string;
      description?: string;
      enum?: string[];
    }
  >;
  required?: string[];
}

export interface SkillTool {
  name: string;
  description: string;
  input_schema: SkillToolInputSchema;
  http: SkillToolHttpConfig;
  requires_confirmation: boolean;
}

export interface Skill {
  id: number;
  code: string;
  name: string;
  description: string;
  system_prompt: string;
  tools: SkillTool[];
  trigger_phrases: string[];
  is_active: boolean;
  requires_admin: boolean;
  organization_id: number | null;
  display_order: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  /** true = system skill (managed via .md files), UI must not edit */
  is_readonly: boolean;
  triggers: SkillTrigger[];
}

export interface SkillListItem {
  id: number;
  code: string;
  name: string;
  description: string;
  is_active: boolean;
  tools_count: number;
  trigger_phrases: string[];
  organization_id: number | null;
  created_at: string;
  /** true = system skill (managed via .md files), UI must not edit */
  is_readonly: boolean;
  triggers_count: number;
}

export interface SkillListResponse {
  items: SkillListItem[];
  total: number;
}

export interface SkillCreateRequest {
  code: string;
  name: string;
  description?: string;
  system_prompt?: string;
  trigger_phrases?: string[];
  triggers?: SkillTrigger[];
  tools?: SkillTool[];
  is_active?: boolean;
  requires_admin?: boolean;
  display_order?: number;
}

/** Only editable fields — excludes read-only server fields */
export interface SkillUpdateRequest {
  name?: string;
  description?: string;
  system_prompt?: string;
  tools?: SkillTool[];
  trigger_phrases?: string[];
  triggers?: SkillTrigger[];
  is_active?: boolean;
  requires_admin?: boolean;
  display_order?: number;
}

export interface SkillTestResult {
  tool: string;
  status: 'ok' | 'error' | 'skip';
  http_status?: number;
  message?: string;
}

export interface SkillTestResponse {
  skill_code: string;
  tools_total: number;
  ok: number;
  errors: number;
  skipped: number;
  results: SkillTestResult[];
}

export interface SkillAnalyticsItem {
  skill_code: string;
  conversations: number;
  messages: number;
  total_tokens: number;
}

export interface SkillAnalyticsResponse {
  period_days: number;
  skills: SkillAnalyticsItem[];
}

export interface SkillTemplate {
  code: string;
  name: string;
  description: string;
  tools_count: number;
}

export interface SkillTemplateListResponse {
  templates: SkillTemplate[];
}

export interface SkillTemplateGenerateParams {
  template_code: string;
  entity_code: string;
  entity_name: string;
}

/** Map skill code to lucide icon name */
export function getSkillIcon(code: string): string {
  if (code.includes('b2b')) return 'Plug';
  if (code.includes('entity')) return 'Blocks';
  if (code.includes('payment')) return 'CreditCard';
  return 'Bot';
}
